import React from "react";
import { useTranslation } from "react-i18next";
import { assetUrl } from "../../utils/assetUrl";

/** The DesignMyNight homepage the logo sends the guest to. */
export const DMN_HOME_URL = "https://www.designmynight.com/";

interface DmnLogoLinkProps {
  /**
   * The imported logo asset. Passed through `assetUrl` here rather than by the
   * caller, so an embedded MFE cannot forget the prefix and 404 the image.
   */
  readonly src: string;
  /** Classes for the wrapping anchor, used to keep it layout-neutral. */
  readonly className?: string;
  /** Classes for the logo image itself. */
  readonly imgClassName?: string;
}

/**
 * The DesignMyNight logo, linking to the DMN homepage in a new tab.
 *
 * `rel="noopener noreferrer"` is required alongside `target="_blank"`: without
 * it the opened page can reach back through `window.opener`.
 *
 * The accessible name comes from the image's `alt`, not an `aria-label` on the
 * anchor, so the link is named by native HTML rather than duplicated ARIA. It
 * names the destination rather than the asset, per WCAG 2.4.4 Link Purpose —
 * "DMN Logo" would tell a screen reader user nothing about where the link goes.
 *
 * Wrapping an image in an anchor makes the anchor the flex/block child in its
 * parent's layout, so callers pass `className` to keep the anchor transparent
 * (e.g. `inline-flex shrink-0` inside a flex row).
 */
const DmnLogoLink: React.FC<DmnLogoLinkProps> = ({ src, className = "", imgClassName = "" }) => {
  const { t } = useTranslation();

  return (
    <a href={DMN_HOME_URL} target="_blank" rel="noopener noreferrer" className={className}>
      <img src={assetUrl(src)} alt={t("dmn_logo_link_label", "DesignMyNight")} className={imgClassName} />
    </a>
  );
};

export default DmnLogoLink;
